import { ModelRow } from "@mcleod/core";
import { Button, CityState, ClickEvent, MapPin, TableSelectionEvent, Textbox } from "@mcleod/components";
import { NoMap } from "@mcleod/components/src/components/map/NoMap";
import { TrimbleMap } from "@mcleod/components/src/components/map/TrimbleMap";
import { AutogenLayoutMobileCommPositionAndMapView } from "./autogen/AutogenLayoutMobileCommPositionAndMapView";
import { MobileCommPositionList } from "./MobileCommPositionList";
import { MobileCommPositionTable } from "./MobileCommPositionTable";
import { ActionButtonType } from "./MobileCommPositionToolPanel";

export class MobileCommPositionAndMapView extends AutogenLayoutMobileCommPositionAndMapView {
    private _pinMap = {};

    async onLoad() {
        this.addLayoutLoadListener(() => {
            this.layoutMobileCommPositionList.addLayoutLoadListener(async () => {
                (this.layoutMobileCommPositionList as MobileCommPositionList).mapAllLandmarks = () => this.mapAllLandmarks();
                await this.layoutMobileCommPositionList.searchList();
            });

            [
                { layout: this.layoutMobileCommPositionList.layoutAll },
                { layout: this.layoutMobileCommPositionList.layoutTractors },
                { layout: this.layoutMobileCommPositionList.layoutDrivers },
                { layout: this.layoutMobileCommPositionList.layoutTrailers }
            ].forEach(({ layout }, i) => {
                layout.addLayoutLoadListener(() => {
                    layout.tableMobileCommPosition.addFilterChangedListener(() => { this.mapAllLandmarks(); });
                    layout.tableMobileCommPosition.addSelectionListener((event: TableSelectionEvent) => this.onSelectionChanged(event));
                    (layout as MobileCommPositionTable).searchList = async () => await this.layoutMobileCommPositionList.searchList();
                    (layout as MobileCommPositionTable).removeCircle = () => this.positionMap.removeCircle();

                    this.positionMap.createMapCallback = () => {
                        this.mapAllLandmarks();
                    }

                    layout.buttonFindNear.addClickListener(() => {
                        const rows = this.layoutMobileCommPositionList.layoutAll.tableMobileCommPosition.filteredRows;
                        const findNearData = layout.buttonFindNearOnClick(rows);
                        this.layoutMobileCommPositionList.unfilteredData = findNearData;
                        this.mapAllLandmarks();
                        layout.isFindNearData = true;

                        if (!(this.positionMap.map instanceof NoMap)) {
                            const cityStateSelectedItem = layout.textboxCity.textCombined.getFirstLookupModelData();
                            const latitude = cityStateSelectedItem.get("latitude");
                            const longitude = cityStateSelectedItem.get("longitude");
                            const radius = Number(layout.textboxRadius.text);
                            this.positionMap.showCircle(Math.abs(latitude), -Math.abs(longitude), radius);
                        }
                    });
                });
            });

            this.layoutMobileCommPositionList.tabAll.addAfterSelectionListener((event) => {
                this.mapAllLandmarks();
            })

            this.layoutMobileCommPositionList.tabsetMobileCommPositionList.addAfterTabSelectionListener(event => {
                this.mapAllLandmarks();
            });

            this.layoutMobileCommPositionList.buttonSearch.addClickListener(async event => {
                await this.layoutMobileCommPositionList.searchList()
                await this.populatePositionTable();
            });
        });
    }

    private async populatePositionTable() {
        [
            { layout: this.layoutMobileCommPositionList.layoutAll },
            { layout: this.layoutMobileCommPositionList.layoutTractors },
            { layout: this.layoutMobileCommPositionList.layoutDrivers },
            { layout: this.layoutMobileCommPositionList.layoutTrailers }
        ].forEach(({ layout }, i) => {
            const findNearButton = layout._toolsPanel.getButton(ActionButtonType.FINDNEAR);
            findNearButton.enabled = this.layoutMobileCommPositionList.switchCurrentPositions.checked;

            if (this.layoutMobileCommPositionList.switchCurrentPositions.checked) {
                findNearButton.tooltip = "FindNear";
            } else {
                layout.setFindNearColumnVisible(false);
                layout.removeCircle();

                layout.textboxRadius.visible = false;
                layout.textboxCity.visible = false;
                layout.buttonFindNear.visible = false;
                findNearButton.tooltip = "FindNear can only be calculated against current position records";

                layout.tableMobileCommPosition.applySort("position_date", "desc");
            }
        });

    }

    private onSelectionChanged(event: TableSelectionEvent) {
        if (this.positionMap.map instanceof NoMap) {
            return;
        }

        const modelRow = event.newRow?.data as ModelRow;

        if (modelRow != null) {
            const activeTab = this.layoutMobileCommPositionList.tabsetMobileCommPositionList.getActiveTab();
            const layout = activeTab.components[0] as MobileCommPositionTable
            const table = layout.tableMobileCommPosition;

            this.positionMap.removeAllPins();
            table.selectedRows.forEach(row => {
                this.addPinAtLocation(row.data, table.selectedRows.length == 1);
            })
            this.positionMap.fitPins();
            this.setZoomLevel();
        } else {
            this.mapAllLandmarks();
        }
    }

    private determineMarkerImage(modelRow: ModelRow): string {
        const image = "dot-green";
        const mcUnitType = modelRow.get("mc_unit_type");
        switch (mcUnitType) {
            case "D": {
                return "pilot";
            }
            case "T":
            case "B":
                {
                    const direction = modelRow.get("direction") as string;
                    const ignition = modelRow.get("ignition_on_flag");

                    if (ignition === "Y") {
                        if (direction && (direction.startsWith("E") || direction.startsWith("N"))) {
                            return "truck_blue_e";
                        } else {
                            return "truck_blue";
                        }
                    } else {
                        if (direction && (direction.startsWith("E") || direction.startsWith("N"))) {
                            return "truck_green_e";
                        } else {
                            return "truck_green";
                        }
                    }
                }
            case "L": {
                return "trailer_red";
            }
            default: {
                return "dot-green";
            }
        }
        return image;
    }

    private addPinAtLocation(modelRow: ModelRow, focusOnPin: boolean) {
        if (this.positionMap.map instanceof NoMap) {
            return;
        }

        const lat = modelRow.get("latitude");
        const lon = modelRow.get("longitude");

        if (lat && lon) {
            const pin = new MapPin(lat, lon);
            pin.layoutName = null;
            pin.pinId = modelRow.get("id");
            pin.image = this.determineMarkerImage(modelRow);
            pin.tooltip = this.buildTooltip(modelRow);
            pin.clusterable = false;
            pin.onClick = (e: ClickEvent) => {
                this.mapPinOnClick?.(e, pin);
            }

            if (focusOnPin) {
                if (!this._pinMap.hasOwnProperty(pin.pinId)) {
                    this._pinMap[pin.pinId] = pin;
                }
                this.positionMap.addPin(pin);
                this.positionMap.setCenter(lat, Math.abs(lon));
                this.positionMap.setZoom(15);
            } else {
                this.positionMap.addPin(pin);
                this._pinMap[pin.pinId] = pin;
            }
        }
    }

    private buildTooltip(modelRow: ModelRow): string {
        let tooltip = "";
        const mcUnitType = modelRow.get("mc_unit_type");

        if ("D" === mcUnitType) {
            tooltip += "Driver: " + modelRow.get("mc_unit_no") + "\n";
        } else if ("T" === mcUnitType || "B" === mcUnitType) {
            tooltip += "Tractor: " + modelRow.get("mc_unit_no") + "\n";
        } else if ("L" === mcUnitType) {
            tooltip += "Trailer: " + modelRow.get("mc_unit_no") + "\n";
        }

        tooltip += "Date/time: " + modelRow.get("position_date") + "\n";
        let ignitionString = "";
        if (modelRow.get("ignition_on_flag") === "Y") {
            ignitionString = "Yes";
        } else if (modelRow.get("ignition_on_flag") === "N") {
            ignitionString = "No";
        }
        tooltip += "Ignition: " + ignitionString + "\n";
        tooltip += "Speed: " + modelRow.get("speed") + "\n";
        tooltip += "Direction: " + modelRow.get("direction") + "\n";
        tooltip += "City: " + modelRow.get("near_s_city") + "\n";

        return tooltip;
    }

    private mapAllLandmarks(resetZoom: boolean = true) {
        if (this.positionMap.map instanceof NoMap) {
            return;
        }

        this.positionMap.removeAllPins();
        const activeTab = this.layoutMobileCommPositionList.tabsetMobileCommPositionList.getActiveTab();
        const layout = activeTab.components[0] as MobileCommPositionTable
        const table = layout.tableMobileCommPosition;

        const rows = (table.selectedRows && table.selectedRows.length > 0) ? table.selectedRows : table.filteredRows;

        this.positionMap.removeCircle();

        rows.forEach(tableRow => {
            const modelRow = tableRow.data as ModelRow;
            this.addPinAtLocation(modelRow, false);
        });

        if (resetZoom) {
            this.positionMap.fitPins();
            this.setZoomLevel();
        }
    }

    private setZoomLevel() {
        if (this.positionMap.map instanceof TrimbleMap) {
            this.positionMap.setZoom(3);
        }
    }

    private mapPinOnClick(e: ClickEvent, pin: MapPin, setZoom: boolean = true) {
        const activeTab = this.layoutMobileCommPositionList.tabsetMobileCommPositionList.getActiveTab();
        const layout = activeTab.components[0] as MobileCommPositionTable
        const table = layout.tableMobileCommPosition;

        if (this._pinMap.hasOwnProperty(pin.pinId)) {
            console.log("count: " + table.selectedRows.length);
            table.rows.forEach(tableRow => {
                const tableModelRow = tableRow.data as ModelRow;
                if (pin.pinId == tableModelRow.get("id")) {
                    tableRow.scrollIntoView();
                    this.positionMap.setCenter(pin.latitude, Math.abs(pin.longitude));
                    if (setZoom) {
                        this.positionMap.setZoom(15);
                    }
                    return;
                }
            });
        }
    }
}
